/**
 * CteSection — renders the WITH clause as an expandable top section: the
 * section shell (expand/collapse-all, the `[+ add CTE]` `<CteAddPopover>`,
 * WITH-from-scratch synthesis when the source has no clause yet — mirroring
 * the GroupBy / OrderBy synthesis path) plus one `<CteCard>` per binding.
 *
 * All per-card machinery lives in `CteCard.tsx` (split out of this file):
 * inline rename via `<CteEditableName>`, the remove flow with its synchronous
 * reference scan (`useCteReferenceScan` — unreferenced ⇒ immediate remove;
 * referenced ⇒ confirm dialog; scan unavailable ⇒ remove hard-disabled), and
 * — for SELECT-shaped bodies when `editContext` is supplied AND a schema
 * snapshot exists — a derived body editContext that makes the CTE body EDITABLE
 * in place and DRILLABLE via the shared drill stack (focus button →
 * `pushDrill`, `feature:vqe_drill { source: 'cte' }`). On the compact surface
 * (`compact` prop, threaded through to the cards) the body edit/drill
 * affordances are suppressed; everything else is unchanged there. Recursive
 * CTEs are FIRST-CLASS (gate lift, decision 5): the clause title still reads
 * "With (recursive)" (keyword-derived) and the footer `[+ add CTE]` is enabled,
 * exactly like a non-recursive clause; the per-card recursion badge is
 * reference-based (decision 4).
 *
 * Without `editContext` (or without a snapshot) the disabled VQE-RO
 * scaffolding renders unchanged.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.
 */
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { Button } from '../../../../../components/ui/button';
import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';
import { ClauseCard } from './ClauseCard';
import { CteAddPopover } from './CteAddPopover';
import { CteCard, cteExpansionKey } from './CteCard';
import type { CteBinding, CteOutline } from './extractCtes';

export interface CteSectionProps {
  readonly bufferText: string;
  readonly outline: CteOutline;
  readonly options: ParseOptions;
  readonly statementId: string;
  readonly onEditAsSql: () => void;
  readonly editContext?: VqeEditContext;
  /**
   * Tab id a CTE-body drill entry is attributed to. Threaded from
   * `SelectRenderer` (same `drillTabId ?? statement.id` fallback family as
   * `subqueryCtx.drillTabId`) down to each `CteCard`'s focus/drill button.
   */
  readonly drillTabId?: string;
  /**
   * Compact surfaces (compact popover + inline subqueries): populated CTE
   * cards keep their read-only body (no compact CTE drill). With no WITH clause
   * yet this section renders nothing on either surface — the Add-CTE entry is
   * mounted by `SelectRenderer` (SELECT-header kebab when compact, the FROM
   * card's action line otherwise).
   */
  readonly compact?: boolean;
}

export function CteSection({
  bufferText,
  outline,
  options,
  statementId,
  onEditAsSql,
  editContext,
  drillTabId,
  compact,
}: CteSectionProps) {
  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();
  // When the source has no WITH clause AND we can edit (with snapshot), render
  // an empty shell so the user can synthesize one via [+ add CTE]. Otherwise,
  // the VQE-RO null-return is preserved.
  // No WITH clause yet: nothing here. Compact surfaces mount the kebab
  // Add-CTE menu on the SELECT header; the full layout puts "Add CTE" on the
  // FROM card's action line (`SelectRenderer`). Once a CTE is added, the
  // populated WITH card below takes over this slot.
  if (!outline.hasWith) return null;

  const cteCount = outline.bindings.length;
  return (
    <ClauseCard
      title={outline.isRecursive ? 'With (recursive)' : 'With'}
      testId="vqe-cte-section"
      accessory={
        cteCount >= 2 ? (
          <CteBulkToggle statementId={statementId} drillTabId={drillTabId} bindings={outline.bindings} />
        ) : undefined
      }
      footer={<CteAddPopover editContext={editReady ? editContext : undefined} />}
    >
      <ul className="flex flex-col gap-2" data-testid="vqe-cte-list">
        {outline.bindings.map((binding, idx) => (
          <li key={`${binding.name}-${idx}`}>
            <CteCard
              bufferText={bufferText}
              binding={binding}
              options={options}
              statementId={statementId}
              index={idx}
              cteCount={cteCount}
              editReady={editReady}
              editContext={editContext}
              onEditAsSql={onEditAsSql}
              drillTabId={drillTabId}
              compact={compact}
            />
          </li>
        ))}
      </ul>
    </ClauseCard>
  );
}

interface CteBulkToggleProps {
  readonly statementId: string;
  readonly drillTabId?: string;
  readonly bindings: readonly CteBinding[];
}

/**
 * Bulk expand/collapse control for the WITH clause header. Shown only when the
 * clause declares >= 2 CTEs (the caller gates the mount). It reads each card's
 * existing per-card expansion key (`cteExpansionKey` — tab-scoped, FIX-B; see
 * the builder's docblock in CteCard.tsx) so the derived "all expanded?" state
 * stays in lock-step with individual toggles, then flips every key to the
 * opposite on click via the same `setExpansion` action the per-card chevrons
 * use. `data-mode` reflects the NEXT action so the e2e/unit specs (and the
 * icon) can read intent without re-deriving state.
 */
function CteBulkToggle({ statementId, drillTabId, bindings }: CteBulkToggleProps) {
  const setExpansion = useVqeStore((s) => s.setExpansion);
  const expandedStates = useVqeStore(
    useShallow((s) =>
      bindings.map((b) => s.expansion[cteExpansionKey(drillTabId, statementId, b.name)] ?? false),
    ),
  );
  const { track } = useAnalytics();

  const allExpanded = expandedStates.every(Boolean);
  // The toggle always drives every card to a single uniform state: if all are
  // already expanded, collapse all; otherwise expand all (any mixed/collapsed
  // state expands). `nextMode` is the action this click will perform.
  const nextMode: 'expand' | 'collapse' = allExpanded ? 'collapse' : 'expand';

  const onToggle = (): void => {
    const open = nextMode === 'expand';
    for (const binding of bindings) {
      setExpansion(cteExpansionKey(drillTabId, statementId, binding.name), open);
    }
    track('feature:vqe_cte_bulk_toggle', { mode: nextMode });
  };

  const label =
    nextMode === 'expand' ? vqeCopy.actions.expandAllCtes : vqeCopy.actions.collapseAllCtes;

  return (
    <VqeTooltip content={label}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 px-2 text-xs text-muted-foreground"
        data-testid="vqe-cte-bulk-toggle"
        data-mode={nextMode}
        aria-label={label}
        onClick={onToggle}
      >
        {nextMode === 'expand' ? (
          <ChevronsUpDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronsDownUp className="h-3.5 w-3.5" />
        )}
      </Button>
    </VqeTooltip>
  );
}

// Re-export the StatementInfo helper so hosts can construct nested statements
// when wiring the recursive renderer.
export type { StatementInfo };
